import _ from 'underscore';

import {SchemaTag} from 'folder-items/FileConstants';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableConstants';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {Schema} from 'schemas/SchemaConstants';

import {ArchiveRecordGraphQL} from '../../../graphql-fragments/app-core/platform/archivable/__generated/ArchiveRecordGraphQL';
import {ProcessTableLocation} from '../ProcessConstants';
import {makeTaskIdCell} from './MakeProcessCellUtils';
import ProcessOutputStructuredTableAdapter, {
  ProcessOutputStructuredTable,
  ProcessOutputStructuredTableMetadata,
} from './ProcessOutputStructuredTableAdapter';

/**
 * Creates a structured table from a raw Process object
 */

interface CreateOutputTableTaskSchema {
  name: string;
  outputSchema: Schema;
}

interface ProcessOutputBase {
  fields: Array<SchemaTag>;
  archiveRecord: ArchiveRecordGraphQL | null;
  task: {
    id: string;
    humanId: string;
  };
  isPersisted?: boolean;
}

interface ProcessOutputWithID extends ProcessOutputBase {
  id: string;
  humanId: string;
}

type ProcessOutput = ProcessOutputWithID | ProcessOutputBase;

interface CreateOutputTableParams {
  process: {
    id: string;
    name: string;
    processTaskSchema: CreateOutputTableTaskSchema;
    outputs: Array<ProcessOutput>;
  };
  metadata: ProcessOutputStructuredTableMetadata & {tableLocation: ProcessTableLocation};
  selectorsById: SchemaFieldSelectorsDict;
  locked?: boolean;
}

interface CreateOutputTableWithOutputsParams {
  processTaskSchema: CreateOutputTableTaskSchema;
  outputs: Array<ProcessOutput>;
  metadata: ProcessOutputStructuredTableMetadata & {tableLocation: ProcessTableLocation};
  selectorsById: SchemaFieldSelectorsDict;
  locked?: boolean;
}

// The caller can either provide "sourceTasks" (for prepopulating the task chips), or "numRows" (which
// will create empty rows)
type CreateOutputTableWithEmptyRowsParams = {
  outputSchema: Schema;
  metadata: ProcessOutputStructuredTableMetadata & {tableLocation: ProcessTableLocation};
  selectorsById: SchemaFieldSelectorsDict;
} & (
  | {
      numRows?: never;
      sourceTasks: Array<{id: string; humanId: string}>;
    }
  | {
      numRows: number;
      sourceTasks?: never;
    }
);

export function createOutputTableWithEmptyRows(
  params: CreateOutputTableWithEmptyRowsParams
): ProcessOutputStructuredTable {
  const {outputSchema, metadata, selectorsById} = params;
  const outputTableAdapter = ProcessOutputStructuredTableAdapter.getAdapter();
  const createProps = {
    name: outputSchema.name,
    schemas: [{...outputSchema, columnGroup: null}],
    entryId: null,
    metadata: {...metadata},
    selectorsById,
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  };

  const table = outputTableAdapter.createTable(createProps);
  return {
    ...table,
    cid: `${outputSchema.id}-outputs`,
    rows:
      params.sourceTasks != null
        ? params.sourceTasks.map((task, taskIndex) => {
            const row = outputTableAdapter.getBlankRow({}, taskIndex, table.columns);
            return outputTableAdapter.setTaskCell(table.columns, row, makeTaskIdCell(task));
          })
        : _.range(0, params.numRows).map(() => outputTableAdapter.getBlankRow({}, 0, table.columns)),
  };
}

function fillOutputTableWithOutputs({
  outputs,
  table,
}: {
  outputs: Array<ProcessOutput>;
  table: Omit<ProcessOutputStructuredTable, 'cid'>;
}): Omit<ProcessOutputStructuredTable, 'cid'> {
  const adapter = getStructuredTableAdapters()[
    StructuredTableKind.PROCESS_OUTPUT
  ] as ProcessOutputStructuredTableAdapter;
  if (outputs.length === 0) {
    table.rows = [{...adapter.getBlankRow({}, 0, table.columns), isFinalized: false}];
  } else {
    table.rows = outputs.map((output, idx) => ({
      ...adapter.getBlankRow({}, idx, table.columns),
      ...ProcessOutputStructuredTableAdapter.makeRowFromOutput(output, table.columns),
      isFinalized: output.isPersisted ?? true,
      metadata: adapter.getBlankRowMetadata(table.metadata),
      // We provide LUSVs because the structured table framework views a null LSUV as a newly inserted column
      lastSyncedUpstreamValues:
        ProcessOutputStructuredTableAdapter.optimisticallyMakeLastSyncedUpstreamValuesFromOutput(
          output,
          table.columns
        ),
    }));
  }
  return table;
}

export function createOutputTableWithTask({
  processTaskSchema,
  outputs,
  taskId,
  metadata,
  selectorsById,
  locked,
}: CreateOutputTableWithOutputsParams & {taskId: string}): ProcessOutputStructuredTable {
  const adapter = getStructuredTableAdapters()[
    StructuredTableKind.PROCESS_OUTPUT
  ] as ProcessOutputStructuredTableAdapter;

  const createProps = {
    name: processTaskSchema.name,
    schemas: [{...processTaskSchema.outputSchema, columnGroup: null}],
    entryId: null,
    metadata: {...metadata, taskId},
    selectorsById,
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  };

  const table = adapter.createTable(createProps);
  if (metadata.tableLocation === ProcessTableLocation.TASK_DETAILS_OUTPUTS_TAB) {
    table.columns = table.columns.filter((col) => col.columnType !== TableColumnType.PROCESS_TASK);
  }
  if (locked) {
    table.columns = table.columns.map((col) => ({...col, isReadOnly: true}));
  }
  const modifiedTable = fillOutputTableWithOutputs({outputs, table});
  return {...modifiedTable, cid: `${taskId}-outputs`};
}

export function createOutputTableWithOptionalProcess({
  processTaskSchema,
  outputs,
  processId,
  metadata,
  selectorsById,
  locked,
}: CreateOutputTableWithOutputsParams & {processId?: string}): ProcessOutputStructuredTable {
  const adapter = getStructuredTableAdapters()[
    StructuredTableKind.PROCESS_OUTPUT
  ] as ProcessOutputStructuredTableAdapter;
  const createProps = {
    name: processTaskSchema.name,
    schemas: [{...processTaskSchema.outputSchema, columnGroup: null}],
    entryId: null,
    metadata: {...metadata, processId},
    selectorsById,
    structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
  };

  const table = adapter.createTable(createProps);
  if (locked) {
    table.columns = table.columns.map((col) => ({...col, isReadOnly: true}));
  }
  const modifiedTable = fillOutputTableWithOutputs({outputs, table});
  return {...modifiedTable, cid: `${processId ?? 'no-process'}-outputs`};
}

export default function createOutputTable({
  process,
  metadata,
  selectorsById,
  locked,
}: CreateOutputTableParams): ProcessOutputStructuredTable {
  return createOutputTableWithOptionalProcess({
    processId: process.id,
    outputs: process.outputs,
    processTaskSchema: process.processTaskSchema,
    metadata,
    selectorsById,
    locked,
  });
}
